import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@macs-frontend/env';
import { CurrentCompanyService } from '@macs-frontend/core/services/current-company.service';
import { emptyCompany } from '@macs-frontend/features/shared/models/company.model';

/**
 * Stamps X-Company-Uuid on every ms-materials request.
 *
 * ms-materials resolves the tenant as: companyUuid query param →
 * X-Company-Uuid header → FIRST company membership in the JWT groups claim.
 * Without an explicit tenant, a multi-company user's requests land on
 * whichever company happens to be first in their token — for every tenant
 * except that one, lookups fail with 404 "not found in this tenant".
 * The header pins requests to the company of the tenant host being browsed.
 *
 * The meta reducer seeds currentCompany with `emptyCompany`, whose uuid is a
 * hardcoded placeholder rather than null. Stamping that placeholder is worse
 * than stamping nothing: it names a company that exists in no tenant, so
 * ms-materials rejects the whole request 403 "User does not have access to
 * company 22a475df-…" and the list renders "Failed to load materials". Any
 * request that races ahead of LoadCurrentCompany hits this. Falling through
 * to the token's first membership is the pre-existing behaviour and is still
 * authorised against the caller's own memberships, so it cannot leak another
 * tenant's data — it just may pick the wrong one of the caller's companies.
 */
@Injectable()
export class CompanyHeaderInterceptor implements HttpInterceptor {
  constructor(private readonly currentCompanyService: CurrentCompanyService) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (
      environment.materials_api_url &&
      request.url.startsWith(environment.materials_api_url) &&
      !request.headers.has('X-Company-Uuid')
    ) {
      const company = this.currentCompanyService.currentCompany$.value;
      if (company?.uuid && company.uuid !== emptyCompany.uuid) {
        request = request.clone({
          setHeaders: { 'X-Company-Uuid': String(company.uuid) },
        });
      }
    }
    return next.handle(request);
  }
}
